import React from "react";
import { gql } from "@apollo/client";
import { useMutation } from "@apollo/client";
import { Form } from "react-final-form";
import arrayMutators from "final-form-arrays";
import { FieldArray } from "react-final-form-arrays";
import {
  TextField,
  CheckboxField,
  Fieldset,
  SelectField,
  CheckboxGroupField,
} from "@syos-music/form";
import { Dialog } from "@syos-music/common";

import { InputAdornment, MenuItem, Grid } from "@mui/material";
import { LoadingButton } from "@mui/lab";

import IntlTextField from "components/form/IntlTextField";
import PriceField from "components/form/PriceField";

const createProductMutation = gql`
  mutation (
    $name: String!
    $prices: [PriceInput]!
    $isB2B: Boolean!
    $account: String!
    $label: IntlStringInput!
    $displayMode: String!
    $weight: Float!
    $hasShipping: Boolean!
    $hasFeedback: Boolean!
    $hasSoundShaping: Boolean!
    $allowedReturnTypes: [OrderReturn_Type]!
  ) {
    CreateProduct(
      name: $name
      prices: $prices
      isB2B: $isB2B
      account: $account

      label: $label
      displayMode: $displayMode
      weight: $weight

      hasShipping: $hasShipping
      hasSoundShaping: $hasSoundShaping
      hasFeedback: $hasFeedback
      allowedReturnTypes: $allowedReturnTypes
    ) {
      product {
        id
      }
    }
  }
`;

export default function CreateProductDialog({ onSuccess, children }) {
  const [isOpen, setIsOpen] = React.useState(false);

  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);

  const [createProduct] = useMutation(createProductMutation);

  return (
    <>
      {children({ open, close })}

      <Dialog open={isOpen} onClose={close} maxWidth="lg" fullWidth>
        <Form
          mutators={{ ...arrayMutators }}
          onSubmit={async (values) =>
            createProduct({
              variables: {
                ...values,
                weight: parseFloat(values.weight),
              },
            })
              .then(async () => {
                await onSuccess?.();
                close();
              })
              .catch(() => true)
          }
          initialValues={{
            isB2B: false,
            account: "707",
            prices: [
              { currency: "EUR", value: 0 },
              { currency: "USD", value: 0 },
            ],
            displayMode: "default",
            hasShipping: false,
            weight: 0,
            hasSoundShaping: false,
            hasFeedback: false,
            allowedReturnTypes: [],
          }}
          keepDirtyOnReinitialize
        >
          {({ handleSubmit, submitting }) => (
            <form onSubmit={handleSubmit} autoComplete="off" noValidate>
              <Dialog.Title color="primary">Créer un produit</Dialog.Title>

              <Dialog.Content>
                <Grid container>
                  <Grid item xs={12} sm={4}>
                    <TextField name="name" required label="Nom machine" />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField name="account" required label="Code comptable" />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <CheckboxField
                      name="isB2B"
                      label="Produit destiné à des professionnels"
                      helperText="Utilisé pour filtrer l’affichage des produits dans Lapidou"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Fieldset
                      legend="Prix (HT)"
                      name="prices"
                      collapsible={false}
                    >
                      <FieldArray name="prices">
                        {({ fields }) => (
                          <Grid container>
                            {fields.map((fieldName, idx) => (
                              <Grid key={idx} item xs={12} sm={6}>
                                <PriceField name={fieldName} required />
                              </Grid>
                            ))}
                          </Grid>
                        )}
                      </FieldArray>
                    </Fieldset>
                  </Grid>

                  <Grid item xs={12}>
                    <Fieldset legend="Panier" collapsible={false}>
                      <Grid container>
                        <Grid item xs={12}>
                          <IntlTextField
                            name="label"
                            required
                            label="Libellé"
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <SelectField
                            name="displayMode"
                            label="Type d'affichage"
                            helperText="Comportement de l’affichage du produit dans le panier"
                            required
                          >
                            <MenuItem value="default">default</MenuItem>

                            {[
                              "originalsMouthpiece",
                              "signatureMouthpiece",
                              "customMouthpiece",
                              "mouthpieceLigature",
                              "mouthpieceCap",
                              "accessoriesPack",
                              "cushionsPack",
                            ].map((displayMode) => (
                              <MenuItem key={displayMode} value={displayMode}>
                                {displayMode}
                              </MenuItem>
                            ))}
                          </SelectField>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <CheckboxField
                            name="hasShipping"
                            label="Livraison"
                            helperText="Des frais de livraison sont-ils applicables si ce produit est présent dans le panier ?"
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            type="number"
                            name="weight"
                            required
                            label="Poids"
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  Kg
                                </InputAdornment>
                              ),
                              step: 0.01,
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <CheckboxField
                            name="hasSoundShaping"
                            label="Sound shaping"
                            helperText="Est-il possible d’ajouter des sound shapings à de produit lorsq'il est présent dans un panier ?"
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <CheckboxField
                            name="hasFeedback"
                            label="Feedback"
                            helperText="Autoriser le formulaire de feedback sur ce produit ?"
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <CheckboxGroupField
                            label="Types de retours authorisés"
                            name="allowedReturnTypes"
                          >
                            <CheckboxGroupField.Option
                              name="EXCHANGE"
                              label="Échange"
                            />
                            <CheckboxGroupField.Option
                              name="REFUND"
                              label="Remboursement"
                            />
                          </CheckboxGroupField>
                        </Grid>
                      </Grid>
                    </Fieldset>
                  </Grid>
                </Grid>
              </Dialog.Content>

              <Dialog.Actions>
                <LoadingButton type="submit" loading={submitting}>
                  Valider
                </LoadingButton>
              </Dialog.Actions>
            </form>
          )}
        </Form>
      </Dialog>
    </>
  );
}
